import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Printer, Share2, Sparkles, Leaf, Quote, Lock, Download, Mail } from "lucide-react";
import { isPremium } from "@/components/PremiumGate";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";

export default function Blueprint() {
    const { user } = useAuth();
    const [bp, setBp] = useState(null);
    const [busy, setBusy] = useState(true);
    const printRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/blueprint/latest");
            setBp(data);
            setBusy(false);
        } catch {
            // Not generated yet — generate now
            try {
                setBusy(true);
                const { data } = await api.post("/blueprint/generate");
                setBp(data);
            } catch (e) {
                toast.error("We couldn't compose your blueprint. Please try again.");
            } finally {
                setBusy(false);
            }
        }
    };
    useEffect(() => { load(); }, []);

    const downloadPdf = async () => {
        try {
            const token = localStorage.getItem("eden_jwt");
            const r = await fetch(`${API}/blueprint/pdf`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: "include",
            });
            if (!r.ok) throw new Error(await r.text());
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `eden-blueprint-${(user?.name || "steward").split(" ")[0].toLowerCase()}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Your Blueprint is downloading.");
        } catch (e) {
            toast.error("Couldn't render the PDF right now.");
        }
    };

    const regenerate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/blueprint/generate?force=true", null, { timeout: 180000 });
            setBp(data);
            toast.success("A fresh blueprint has been composed for you.");
        } catch (err) {
            // If the edge times out, the backend usually still finishes and caches.
            // Poll /latest a few times to recover gracefully.
            toast.message("Still composing… I'll check again in a moment.");
            const started = Date.now();
            let recovered = false;
            while (Date.now() - started < 90000) {
                await new Promise((r) => setTimeout(r, 6000));
                try {
                    const { data: latest } = await api.get("/blueprint/latest");
                    if (latest && (!bp || latest.blueprint_id !== bp.blueprint_id || latest.created_at !== bp.created_at)) {
                        setBp(latest);
                        toast.success("A fresh blueprint has been composed for you.");
                        recovered = true;
                        break;
                    }
                } catch (e) { console.debug("blueprint poll retry", e?.message); /* keep polling */ }
            }
            if (!recovered) toast.error("The composition took longer than expected. Please try again shortly.");
        } finally { setBusy(false); }
    };

    const share = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({ title: "My Cultiv Stewardship Blueprint", url });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success("Link copied to clipboard.");
            }
        } catch (e) { console.debug("blueprint share fallback", e?.message); /* ignore — share is best-effort */ }
    };

    if (busy || !bp) {
        return (
            <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4">
                <Sparkles className="w-6 h-6 text-[hsl(var(--eden-gold))] animate-pulse" strokeWidth={1.2} />
                <div className="font-serif text-2xl text-muted-foreground">Composing your personal blueprint…</div>
                <div className="text-xs text-muted-foreground">This may take a moment.</div>
            </div>
        );
    }

    const r = bp.report || {};
    const firstName = user?.name?.split(" ")[0] || "friend";
    const premium = isPremium(user);
    const isPreview = bp.tier === "free" || r.is_preview === true;

    // Free preview view — a beautiful teaser + a clear invitation to Premium.
    if (isPreview) {
        return (
            <div className="space-y-14">
                <motion.section
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="hairline rounded-lg p-10 md:p-16 relative overflow-hidden"
                >
                    <div className="absolute -right-16 -top-16 opacity-10">
                        <Leaf className="w-64 h-64 text-[hsl(var(--eden-forest))]" strokeWidth={0.5} />
                    </div>
                    <div className="text-xs uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-4">
                        Your Blueprint · Free Summary
                    </div>
                    <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] max-w-3xl">
                        A first glimpse of what has been quietly true in <em className="text-[hsl(var(--eden-gold))]/90">{firstName}</em>.
                    </h1>
                    <div className="mt-8 flex flex-wrap gap-6 text-sm text-muted-foreground">
                        <div><span className="uppercase tracking-widest text-xs">Overall</span> · <span data-testid="bp-overall-score">{bp.overall_score}/100</span></div>
                        <div><span className="uppercase tracking-widest text-xs">Season</span> · {bp.seasons?.join(", ") || "—"}</div>
                    </div>
                    <p className="mt-10 text-lg leading-relaxed max-w-3xl" data-testid="bp-summary">
                        {r.overall_summary}
                    </p>
                </motion.section>

                {r.patterns?.length > 0 && (
                    <section>
                        <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground mb-4">Two patterns we noticed</div>
                        <ul className="space-y-4 max-w-3xl">
                            {r.patterns.map((p, i) => (
                                <li key={i} className="flex gap-5 items-baseline">
                                    <span className="font-serif text-2xl text-[hsl(var(--eden-gold))]/70 leading-none">·</span>
                                    <span className="text-lg leading-relaxed">{p}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {r.one_next_step && (
                    <section className="hairline rounded-lg p-8 md:p-10 max-w-3xl">
                        <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-3">Your first next step</div>
                        <p className="font-serif text-2xl md:text-3xl leading-snug" data-testid="bp-one-next-step">{r.one_next_step}</p>
                    </section>
                )}

                {/* Premium invitation */}
                <section className="hairline rounded-lg p-10 md:p-14 bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] relative overflow-hidden" data-testid="blueprint-premium-invite">
                    <div className="absolute -right-10 -top-10 opacity-[0.08]">
                        <Sparkles className="w-64 h-64 text-[hsl(var(--eden-gold))]" strokeWidth={0.4} />
                    </div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-4">
                        <Lock className="w-3 h-3" strokeWidth={1.5} /> Premium unlocks the full Blueprint
                    </div>
                    <h3 className="font-serif text-3xl md:text-5xl leading-[1.05] max-w-3xl">
                        The full 10–15 page personalized Stewardship Blueprint.
                    </h3>
                    <p className="mt-6 text-[hsl(var(--eden-cream))]/75 leading-relaxed max-w-2xl">
                        Deeper insight into every garden, four KJV scriptures chosen for you, five reflection questions,
                        a personalized prayer, a full 30-day plan, weekly action steps, and a printable PDF.
                        Plus unlimited Tend, pattern insights, and every future stewardship module.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/membership" data-testid="bp-see-premium-btn">
                            <Button className="bg-[hsl(var(--eden-cream))] text-[hsl(var(--eden-forest))] hover:bg-white rounded-md h-11 px-6 text-xs uppercase tracking-[0.3em]">
                                See Premium
                            </Button>
                        </Link>
                    </div>
                </section>
            </div>
        );
    }

    // Premium — full report

    return (
        <div className="space-y-16">
            {/* Print-only styles */}
            <style>{`@media print {
                header, footer, [data-noprint], nav { display: none !important; }
                main { padding: 0 !important; }
                .print-page { break-inside: avoid; page-break-inside: avoid; }
                body { background: #fff !important; color: #1F3A2E !important; }
                .hairline { border: 1px solid #1F3A2E30 !important; }
                * { color-adjust: exact; -webkit-print-color-adjust: exact; }
            }`}</style>

            {/* Actions */}
            <div className="flex flex-wrap gap-2" data-noprint>
                <Button onClick={() => window.print()} data-testid="blueprint-print-btn" variant="outline" className="rounded-md border-foreground/15">
                    <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> Print
                </Button>
                <Button onClick={downloadPdf} data-testid="blueprint-pdf-btn" variant="outline" className="rounded-md border-foreground/15">
                    <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Download PDF
                </Button>
                <Button onClick={share} data-testid="blueprint-share-btn" variant="outline" className="rounded-md border-foreground/15">
                    <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Share
                </Button>
                <Button onClick={regenerate} disabled={!premium} data-testid="blueprint-refresh-btn" variant="ghost">
                    {premium ? "Recompose" : "Recompose (Premium)"}
                </Button>
            </div>

            {/* Cover */}
            <motion.section
                ref={printRef}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="print-page hairline rounded-md p-10 md:p-16 relative overflow-hidden"
            >
                <div className="absolute -right-16 -top-16 opacity-10">
                    <Leaf className="w-64 h-64 text-[hsl(var(--eden-forest))]" strokeWidth={0.5} />
                </div>
                <div className="text-xs uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-4">
                    A Personal Stewardship Blueprint
                </div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] max-w-3xl">
                    For <em className="text-[hsl(var(--eden-gold))]/90">{firstName}</em> — a garden entrusted, tenderly seen.
                </h1>
                <div className="mt-8 flex flex-wrap gap-6 text-sm text-muted-foreground">
                    <div><span className="uppercase tracking-widest text-xs">Overall</span> · <span data-testid="bp-overall-score">{bp.overall_score}/100</span></div>
                    <div><span className="uppercase tracking-widest text-xs">Season</span> · {bp.seasons?.join(", ") || "—"}</div>
                    <div><span className="uppercase tracking-widest text-xs">Composed</span> · {new Date(bp.created_at).toLocaleDateString(undefined, { dateStyle: "long" })}</div>
                </div>
                <p className="mt-10 text-lg leading-relaxed max-w-3xl prose-cultiv dropcap" data-testid="bp-summary">
                    {r.overall_summary}
                </p>
            </motion.section>

            {/* Strongest & Cultivating */}
            <div className="grid md:grid-cols-2 gap-4 print-page">
                <Section title="Strongest areas" subtitle="Where fruit is already visible">
                    <ul className="space-y-6">
                        {(r.strongest_gardens || []).map((g, i) => (
                            <li key={i}>
                                <div className="font-serif text-2xl">{g.garden}</div>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{g.insight}</p>
                            </li>
                        ))}
                    </ul>
                </Section>
                <Section title="Areas ready for cultivation" subtitle="Not broken — fertile soil">
                    <ul className="space-y-6">
                        {(r.cultivating_gardens || []).map((g, i) => (
                            <li key={i}>
                                <div className="font-serif text-2xl">{g.garden}</div>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{g.gentle_note}</p>
                            </li>
                        ))}
                    </ul>
                </Section>
            </div>

            {/* Patterns */}
            <Section title="Patterns we noticed" subtitle="Honest, never accusing">
                <ul className="space-y-4">
                    {(r.patterns || []).map((p, i) => (
                        <li key={i} className="flex gap-4 items-baseline">
                            <span className="font-serif text-3xl text-[hsl(var(--eden-gold))]/70">·</span>
                            <span className="text-lg leading-relaxed">{p}</span>
                        </li>
                    ))}
                </ul>
            </Section>

            {/* Scriptures */}
            <Section title="Scriptures for this season" subtitle="From the King James Version">
                <div className="grid md:grid-cols-2 gap-6">
                    {(r.scriptures || []).map((s, i) => (
                        <div key={i} className="hairline rounded-md p-6" data-testid={`bp-scripture-${i}`}>
                            <div className="text-xs uppercase tracking-widest text-[hsl(var(--eden-gold))] mb-3">{s.reference}</div>
                            <blockquote className="font-serif text-xl italic leading-snug">
                                <Quote className="w-4 h-4 inline text-[hsl(var(--eden-gold))]/60 mr-1" strokeWidth={1.2} />
                                {s.verse}
                            </blockquote>
                            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{s.why_this}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Reflection questions */}
            <Section title="Reflection questions" subtitle="Sit with these; there is no rush">
                <ol className="space-y-5">
                    {(r.reflection_questions || []).map((q, i) => (
                        <li key={i} className="flex gap-6 items-baseline">
                            <span className="font-serif text-3xl text-[hsl(var(--eden-gold))]/70">{String(i + 1).padStart(2, "0")}</span>
                            <span className="text-lg leading-relaxed">{q}</span>
                        </li>
                    ))}
                </ol>
            </Section>

            {/* Personalized prayer */}
            <Section title="A prayer written for you">
                <blockquote className="font-serif text-2xl md:text-3xl leading-snug italic max-w-3xl" data-testid="bp-prayer">
                    {r.personalized_prayer}
                </blockquote>
            </Section>

            {/* 30-day plan */}
            <Section title="A 30-day stewardship plan" subtitle="Small, tender, faithful — one week at a time">
                <div className="grid md:grid-cols-2 gap-4">
                    {(r.thirty_day_plan || []).map((w, i) => (
                        <div key={i} className="hairline rounded-md p-6" data-testid={`bp-week-${w.week}`}>
                            <div className="flex items-baseline justify-between mb-2">
                                <div className="text-xs uppercase tracking-widest text-muted-foreground">Week {w.week}</div>
                                <div className="text-xs text-[hsl(var(--eden-gold))] uppercase tracking-widest">{w.focus_garden}</div>
                            </div>
                            <div className="font-serif text-2xl mb-4">{w.theme}</div>
                            <ul className="space-y-2">
                                {(w.practices || []).map((p, j) => (
                                    <li key={j} className="text-sm text-muted-foreground flex gap-2 leading-relaxed">
                                        <span className="text-[hsl(var(--eden-gold))]">·</span>
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Weekly action steps */}
            <Section title="This week's action steps" subtitle="Kingdom-shaped, not productivity-shaped">
                <ul className="space-y-3">
                    {(r.weekly_action_steps || []).map((s, i) => (
                        <li key={i} className="flex gap-4 items-start">
                            <span className="font-serif text-2xl text-[hsl(var(--eden-gold))]/70 leading-none">·</span>
                            <span className="text-lg leading-relaxed">{s}</span>
                        </li>
                    ))}
                </ul>
            </Section>

            {/* Encouragement */}
            <section className="print-page hairline rounded-md p-10 md:p-16 bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))]">
                <div className="text-xs uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-6">A benediction</div>
                <p className="font-serif text-3xl md:text-4xl leading-snug max-w-3xl" data-testid="bp-encouragement">
                    {r.encouragement}
                </p>
                <div className="mt-10 text-xs uppercase tracking-widest text-[hsl(var(--eden-cream))]/60">
                    Cultiv · One garden, faithfully tended.
                </div>
            </section>
        </div>
    );
}

const Section = ({ title, subtitle, children }) => (
    <section className="print-page">
        <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{title}</div>
            {subtitle && <div className="font-serif text-3xl md:text-4xl mt-2">{subtitle}</div>}
        </div>
        {children}
    </section>
);
